import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  sendEmailVerification
} from 'firebase/auth'
import { auth, googleProvider } from '../../config/firebase.js'
import { apiService } from './api.service.js'
import { cacheService } from './cache.service.js'

/**
 * Serviço de autenticação Firebase
 * Gerencia login, logout e estado de autenticação
 */
class AuthService {
  constructor() {
    this.currentUser = null
    this.authToken = null
    /** @type {boolean} Indica se o onAuthStateChanged já disparou ao menos uma vez */
    this._authReady = false
    /** @type {Function|null} Resolve da promise de espera pela autenticação */
    this._authReadyResolve = null
    /** @type {Promise} Promise que resolve quando o Firebase restaura a sessão */
    this._authReadyPromise = new Promise(resolve => {
      this._authReadyResolve = resolve
    })
    this._setupAuthListener()
  }

  _setupAuthListener() {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Configura usuário no cache service
        cacheService.setCurrentUser(user.uid)
        
        this.currentUser = user
        this.authToken = await user.getIdToken()
        this._dispatchAuthChange(true)
      } else {
        this.currentUser = null
        this.authToken = null
        
        // Apenas limpa token - preserva cache de dados
        apiService.invalidateTokenCache()
        cacheService.setCurrentUser(null)
        
        this._dispatchAuthChange(false)
      }

      // Marca auth como pronto na primeira execução do listener
      if (!this._authReady) {
        this._authReady = true
        if (this._authReadyResolve) {
          this._authReadyResolve()
        }
      }
    })
  }

  _dispatchAuthChange(isAuthenticated) {
    window.dispatchEvent(new CustomEvent('auth-state-changed', {
      detail: { isAuthenticated, user: this.currentUser }
    }))
  }

  async loginWithEmail(email, password) {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password)
      
      if (!result.user.emailVerified) {
        await this.logout()
        return { 
          success: false, 
          error: 'Por favor, verifique seu email antes de fazer login.',
          needsVerification: true
        }
      }
      
      return { success: true, user: result.user }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  async registerWithEmail(email, password) {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password)
      await sendEmailVerification(result.user)
      
      return { 
        success: true, 
        user: result.user,
        needsVerification: true
      }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  async loginWithGoogle() {
    try {
      const result = await signInWithPopup(auth, googleProvider)
      return { success: true, user: result.user }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  async logout() {
    try {
      // Apenas limpa token cache - preserva dados do usuário para reuso
      apiService.invalidateTokenCache()
      
      await signOut(auth)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  async getAuthToken() {
    if (this.currentUser) {
      return await this.currentUser.getIdToken()
    }
    return null
  }

  isAuthenticated() {
    return !!this.currentUser
  }

  /**
   * Aguarda o Firebase restaurar a sessão (onAuthStateChanged disparar pela primeira vez).
   * Páginas protegidas devem chamar este método antes de fazer requisições ao backend,
   * garantindo que o token esteja disponível após F5/reload.
   * Se o auth já estiver pronto, resolve imediatamente.
   *
   * @returns {Promise<void>} Resolve quando o estado de autenticação está definido
   */
  waitForAuth() {
    if (this._authReady) return Promise.resolve()
    return this._authReadyPromise
  }

  getCurrentUser() {
    return this.currentUser
  }
}

export const authService = new AuthService()
export default authService